import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import ChatDemo from '../components/ChatDemo';
import PhoneMockup from '../components/PhoneMockup';

const NAV_LINKS = [
  { href: '#caracteristicas', label: 'Características' },
  { href: '#como-funciona', label: 'Cómo funciona' },
  { href: '#planes', label: 'Planes' },
  { href: '#preguntas', label: 'Preguntas' }
];

const FEATURES = [
  {
    icon: '💬',
    title: 'Responde como tú',
    description: 'Conoce tus tratamientos, precios y horarios, y atiende por WhatsApp a toda hora.'
  },
  {
    icon: '📅',
    title: 'Agenda citas sola',
    description: 'Revisa tu disponibilidad y agenda la cita automáticamente, sin que muevas un dedo.'
  },
  {
    icon: '🔔',
    title: 'Recordatorios automáticos',
    description: 'Avisa a tus clientas antes de su cita para que no se te escape ninguna.'
  },
  {
    icon: '🙋',
    title: 'Deriva a una persona si hace falta',
    description: 'Si la conversación se complica, pasa la atención a alguien de tu equipo al toque.'
  }
];

const STEPS = [
  {
    number: '1',
    title: 'Configura tu negocio',
    description: 'Cuéntale a tu bot tus servicios, precios y horarios de atención. Toma unos minutos.'
  },
  {
    number: '2',
    title: 'Conecta tu WhatsApp',
    description: 'Vinculamos el número de WhatsApp de tu negocio — nosotros nos encargamos de la parte técnica.'
  },
  {
    number: '3',
    title: 'Tu bot empieza a responder',
    description: 'Tus clientes escriben, preguntan y agendan citas solos. Tú solo revisas el panel.'
  }
];

const BEFORE_AFTER = [
  {
    before: 'Dejas a una clienta a medio esmaltado para contestar el celular',
    after: 'Atiendes tranquila: el bot responde por ti'
  },
  {
    before: 'Te escriben a las 11pm y al día siguiente ya reservaron en otro lado',
    after: 'Responde en segundos, a cualquier hora'
  },
  {
    before: 'Cuaderno, capturas y audios para saber quién viene mañana',
    after: 'Tu agenda se llena sola, ordenada por profesional'
  }
];

const CONTACT_WHATSAPP = '51949771357';

// Apaga/prende el chip "Reprograma" del hero sin tocar el resto del layout.
const SHOW_RESCHEDULE = true;

const HERO_CHIPS = [
  'Responde por ti',
  'Agenda',
  'Confirma',
  'Recordatorio',
  ...(SHOW_RESCHEDULE ? ['Reprograma'] : []),
  'Ordena tu calendario'
];

const HERO_BENEFITS = [
  { icon: '⚡', label: 'Responde en segundos' },
  { icon: '👥', label: 'Atiende a varias a la vez' },
  { icon: '🚀', label: 'Listo en minutos' }
];

const PLANS = [
  {
    key: 'agenda',
    name: 'Agenda',
    price: 19,
    tagline: 'Para ordenar tu agenda y recuperar clientas, sin IA',
    trialLabel: '14 días gratis',
    features: [
      'Agenda por profesional',
      'Ficha de clienta',
      'Recordatorio con 1 clic',
      'Aviso de clientas por volver',
      'Hasta 3 profesionales'
    ],
    cta: 'Probar gratis 14 días',
    ctaHref: '/register?plan=agenda',
    footNote: 'Sin tarjeta de crédito',
    orderClass: 'order-2 lg:order-1'
  },
  {
    key: 'basico',
    name: 'Básico',
    price: 79,
    tagline: 'Para no perder clientas que te escriben fuera de horario',
    trialLabel: '14 días gratis',
    featuresTitle: 'Todo lo de Agenda, más:',
    features: [
      'Bot que responde y agenda 24/7',
      'Ve los espacios libres de cada profesional',
      SHOW_RESCHEDULE ? 'Confirma y reprograma citas' : 'Confirma citas',
      'Te pasa el chat cuando hace falta',
      'Hasta 150 chats al mes · 3 profesionales'
    ],
    ctaHref: '/register?plan=trial',
    highlighted: true,
    badge: 'Recomendado para empezar',
    orderClass: 'order-1 lg:order-2'
  },
  {
    key: 'estandar',
    name: 'Pro',
    price: 139,
    tagline: 'Para cuando ya tienes más citas, profesionales y anuncios activos',
    trialLabel: '14 días gratis',
    featuresTitle: 'Todo lo de Básico, más:',
    features: ['Hasta 500 chats al mes', 'Hasta 6 profesionales', 'Ideal si publicas anuncios'],
    ctaHref: '/register?plan=trial',
    orderClass: 'order-3 lg:order-3'
  }
];

const AGENDA_PREVIEW = [
  {
    src: '/img/agenda-hoy.webp',
    alt: 'Panel de citas de hoy de Konverchat, agrupadas por profesional con acciones rápidas',
    caption: 'Tu día por profesional'
  },
  {
    src: '/img/agenda-recordatorio.webp',
    alt: 'Mensaje de WhatsApp prellenado recordando una cita a una clienta',
    caption: 'Recuerda con 1 clic'
  },
  {
    src: '/img/agenda-clientas-volver.webp',
    alt: 'Lista de clientas que ya deberían volver a agendar según su ciclo de servicio',
    caption: 'A quién le toca volver'
  }
];

const PERSONAS = [
  {
    emoji: '🌙',
    question: '¿Trabajas solo?',
    answer: 'Tu bot responde aunque tú no puedas.',
    description: 'Atiendes de día, descansas de noche — Konverchat sigue contestando por ti.'
  },
  {
    emoji: '👥',
    question: '¿Tienes equipo?',
    answer: 'Libera a tu gente de las preguntas repetidas.',
    description: 'Precios, horarios, disponibilidad: el bot responde lo de siempre para que tu equipo atienda lo que importa.'
  }
];

const FAQS = [
  {
    q: '¿Necesito saber de tecnología?',
    a: 'No. Configuras tu negocio desde un panel simple, y nosotros nos encargamos de conectar tu WhatsApp.'
  },
  {
    q: '¿Qué pasa con mi WhatsApp personal?',
    a: 'Te recomendamos usar un número dedicado al negocio. Si conectas tu número personal, dejarás de poder usar la app normal de WhatsApp en ese número — te lo explicamos antes de conectar nada.'
  },
  {
    q: '¿Cuánto tarda en estar listo?',
    a: 'Configurar tu negocio toma minutos. La conexión de tu WhatsApp normalmente queda lista en menos de 24 horas.'
  },
  {
    q: '¿Hay periodo de prueba?',
    a: 'Sí, 14 días gratis para probar el bot con tu negocio real antes de elegir un plan.'
  },
  {
    q: '¿Pago algo a Meta?',
    a: 'Con el plan Básico normalmente no; Meta da 1,000 respuestas gratis al mes por número.'
  }
];

const trackLead = () => {
  if (window.fbq) window.fbq('track', 'Lead');
};

const trackContact = () => {
  if (window.fbq) window.fbq('track', 'Contact');
};

const WHATSAPP_DEMO_TEXT = 'Hola, quiero ver cómo funciona';

function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      <header className="bg-white/90 backdrop-blur sticky top-0 z-50 shadow-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <span className="flex items-center gap-2 shrink-0">
            <span className="h-8 w-8 rounded-lg bg-lime-300 text-green-900 font-extrabold flex items-center justify-center text-lg">
              K
            </span>
            <span className="text-2xl font-extrabold text-gray-900 tracking-tight">Konverchat</span>
          </span>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Ingresar
            </Link>
            <Link
              to="/register" onClick={trackLead}
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg px-4 py-2"
            >
              Regístrate gratis
            </Link>
          </div>

          <button
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Abrir menú"
            className="lg:hidden text-gray-700 p-1"
          >
            {menuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-3">
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Ingresar
              </Link>
              <Link
                to="/register"
                onClick={() => {
                  trackLead();
                  setMenuOpen(false);
                }}
                className="bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg px-4 py-2 text-center"
              >
                Regístrate gratis
              </Link>
            </div>
          </div>
        )}
      </header>

      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-rose-100 opacity-70 blur-3xl" />
        <div className="pointer-events-none absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-lime-100 opacity-60 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 pt-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 lg:items-center">
          {/* En móvil, "contents" hace que texto y beneficios actúen como ítems sueltos
              del grid (para poder intercalar el chat demo entre ellos con `order`); en
              escritorio se vuelven un solo bloque natural, sin huecos ni filas forzadas. */}
          <div className="contents lg:flex lg:flex-col">
            <div className="order-1 lg:order-none text-center lg:text-left">
              <span className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                💅 Hecho para spas y centros de estética en Lima
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                Tu agenda <span className="bg-lime-300 px-2 rounded box-decoration-clone">se llena sola</span> por WhatsApp
              </h1>

              <div className="mt-4 flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-1.5 max-w-md mx-auto lg:mx-0">
                {HERO_CHIPS.map((chip) => (
                  <span key={chip} className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">
                    <span className="text-green-600">✓</span> {chip}
                  </span>
                ))}
              </div>

              <p className="mt-5 text-lg max-w-xl mx-auto lg:mx-0">
                <span className="text-gray-800 font-semibold">
                  Cada mensaje que respondes tarde es una clienta que reserva en otro lado.
                </span>{' '}
                <span className="text-gray-600">
                  Konverchat ve los espacios libres de cada profesional y agenda por ti, aunque estés fuera del
                  local.
                </span>
              </p>

              <div className="mt-8 flex flex-col items-center lg:items-start gap-3">
                <Link
                  to="/register?plan=trial" onClick={trackLead}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg px-7 py-3 text-base shadow-md"
                >
                  Pruébalo 14 días gratis
                </Link>
                <a
                  href={`https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent(WHATSAPP_DEMO_TEXT)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackContact}
                  className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  💬 ¿Quieres verlo en acción? Escríbele a nuestro bot →
                </a>
              </div>
            </div>

            {/* Beneficios — tercero en móvil, debajo del texto en escritorio */}
            <div className="order-3 lg:order-none lg:mt-10 text-center lg:text-left">
              <div className="flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-2">
                {HERO_BENEFITS.map((b) => (
                  <span key={b.label} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <span className="text-lg">{b.icon}</span> {b.label}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Paga con Yape o Plin · Sin tarjeta de crédito · 14 días gratis
              </p>
            </div>
          </div>

          {/* Chat demo — segundo en móvil, columna derecha en escritorio */}
          <div className="order-2 lg:order-none">
            <ChatDemo />
          </div>
        </div>
      </section>

      <div className="bg-rose-50/60 border-y border-rose-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm font-medium text-gray-700">
          <span className="flex items-center gap-2"><span className="text-2xl">💅</span> Uñas</span>
          <span className="flex items-center gap-2"><span className="text-2xl">💇</span> Pelo</span>
          <span className="flex items-center gap-2"><span className="text-2xl">🧖</span> Spa</span>
          <span className="flex items-center gap-2"><span className="text-2xl">👣</span> Pedicure</span>
        </div>
      </div>

      <section className="bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            Dedícate a lo importante
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BEFORE_AFTER.map((item) => (
              <Fragment key={item.before}>
                <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4">
                  <span className="text-gray-400 text-lg shrink-0">✕</span>
                  <p className="text-gray-500 text-sm">{item.before}</p>
                </div>
                <div className="flex items-start gap-3 bg-lime-50 border border-lime-300 rounded-xl p-4">
                  <span className="text-green-600 text-lg shrink-0">✓</span>
                  <p className="text-gray-900 text-sm font-medium">{item.after}</p>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            ¿Para quién es Konverchat?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PERSONAS.map((p) => (
              <div key={p.question} className="rounded-2xl bg-rose-50 p-6">
                <span className="text-4xl">{p.emoji}</span>
                <p className="mt-3 text-sm font-semibold text-green-700 uppercase tracking-wide">
                  {p.question}
                </p>
                <h3 className="mt-1 font-bold text-gray-900 text-xl leading-snug">{p.answer}</h3>
                <p className="mt-2 text-gray-600 text-sm">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="caracteristicas" className="bg-gradient-to-b from-white to-rose-50/50 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            Todo lo que necesitas para automatizar tu WhatsApp
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex gap-4">
                <span className="text-3xl shrink-0">{f.icon}</span>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{f.title}</h3>
                  <p className="mt-2 text-gray-600">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="como-funciona" className="bg-gray-50 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            Cómo funciona
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.number} className="text-center">
                <div className="mx-auto h-10 w-10 rounded-full bg-green-600 text-white font-bold flex items-center justify-center">
                  {s.number}
                </div>
                <h3 className="mt-4 font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-2 text-gray-600 text-sm">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="planes" className="bg-white max-w-full scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
          Planes simples, sin sorpresas
        </h2>
        <p className="text-gray-600 text-center mb-6">
          Cambia de plan cuando lo necesites.
        </p>

        <div className="flex flex-col items-center gap-2 mb-10">
          <span className="inline-flex items-center gap-2 bg-lime-100 text-green-800 text-sm font-semibold px-4 py-2 rounded-full">
            🎁 14 días de prueba gratis — sin tarjeta de crédito
          </span>
          <span className="inline-flex items-center gap-2 bg-green-50 text-green-700 text-sm font-medium px-4 py-2 rounded-full border border-green-200">
            💚 Pago por Yape o Plin — sin suscripción automática
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {PLANS.map((plan) => (
            <div
              key={plan.key}
              className={`bg-white rounded-2xl p-6 border relative ${plan.orderClass || ''} ${
                plan.highlighted ? 'border-green-500 ring-2 ring-green-100 shadow-lg' : 'border-gray-200'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              )}
              <p className="font-semibold text-gray-900 text-lg">{plan.name}</p>
              {plan.tagline && <p className="mt-1 text-xs text-gray-500">{plan.tagline}</p>}
              {plan.trialLabel && (
                <span className="mt-3 inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm font-bold px-3 py-1 rounded-full">
                  🎁 {plan.trialLabel}
                </span>
              )}
              <p className="mt-2 text-xs text-gray-400">Después:</p>
              <p className="mt-0.5">
                <span className="text-2xl font-bold text-gray-700">S/{plan.price}</span>
                <span className="text-gray-400 text-sm">/mes</span>
              </p>
              {plan.featuresTitle && (
                <p className="mt-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{plan.featuresTitle}</p>
              )}
              <ul className="mt-3 space-y-1.5">
                {plan.features.map((f) => (
                  <li key={f} className="text-sm text-gray-600 flex items-start gap-1.5">
                    <span className="text-green-600 shrink-0">✓</span> {f}
                  </li>
                ))}
              </ul>
              <Link
                to={plan.ctaHref} onClick={trackLead}
                className={`mt-6 block text-center rounded-lg px-4 py-2.5 font-bold border-2 transition-colors ${
                  plan.highlighted
                    ? 'bg-green-600 hover:bg-green-700 border-green-600 text-white'
                    : 'border-green-600 text-green-700 hover:bg-green-50'
                }`}
              >
                {plan.cta || 'Empezar prueba gratis'}
              </Link>
              <p className="mt-2 text-xs text-gray-400 text-center">{plan.footNote || '14 días gratis, sin tarjeta'}</p>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs text-gray-400 text-center">
          Los mensajes de WhatsApp incluyen 1,000 respuestas gratis al mes por número; si las superas, Meta te cobra directamente.
        </p>

        <div className="mt-12">
          <p className="text-center text-sm font-medium text-gray-500 mb-5">
            Así se ve el plan Agenda por dentro
          </p>
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory sm:overflow-visible sm:justify-center sm:gap-8 pb-2 -mx-6 px-6 sm:mx-0 sm:px-0">
            {AGENDA_PREVIEW.map((item) => (
              <div key={item.caption} className="shrink-0 w-48 snap-center sm:w-52">
                <PhoneMockup>
                  <img src={item.src} alt={item.alt} loading="lazy" className="w-full block" />
                </PhoneMockup>
                <p className="mt-2 text-center text-xs font-medium text-gray-700">{item.caption}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-gray-900 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-4xl mx-auto">
          <div className="text-center sm:text-left">
            <p className="font-bold text-white text-lg">¿Más de 500 chats al mes?</p>
            <p className="text-gray-300 text-sm mt-1">
              Escríbenos y armamos un plan a tu medida.
            </p>
          </div>
          <a
            href={`https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent('Hola, quiero un plan a medida')}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackContact}
            className="shrink-0 bg-lime-300 hover:bg-lime-200 text-green-900 font-bold rounded-lg px-6 py-3 whitespace-nowrap"
          >
            Conversemos →
          </a>
        </div>
        </div>
      </section>

      <section id="preguntas" className="bg-gray-50 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            Preguntas frecuentes
          </h2>
          <div className="space-y-3">
            {FAQS.map((item) => (
              <details
                key={item.q}
                className="group bg-white border border-gray-200 rounded-xl px-5 py-4 open:border-green-300"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-gray-900">
                  {item.q}
                  <span className="ml-4 shrink-0 text-green-600 text-xl leading-none transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-2 text-gray-600 text-sm">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Empieza a responder en WhatsApp automáticamente hoy
          </h2>
          <Link
            to="/register" onClick={trackLead}
            className="mt-6 inline-block bg-lime-300 hover:bg-lime-200 text-green-900 font-bold rounded-lg px-6 py-3"
          >
            Regístrate gratis
          </Link>
        </div>
      </section>

      <footer className="bg-gray-900">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-gray-400 text-center space-y-3">
          <div className="flex justify-center gap-6">
            <a href="/privacidad" className="hover:text-white transition-colors">Aviso de privacidad</a>
            <a href="/terminos" className="hover:text-white transition-colors">Términos de servicio</a>
            <a href="/eliminar-datos" className="hover:text-white transition-colors">Eliminar mis datos</a>
          </div>
          <p>© {new Date().getFullYear()} Konverchat</p>
        </div>
      </footer>

      {/* Botón flotante de WhatsApp — oculto en móvil, donde la barra inferior ya trae
          su propio botón de WhatsApp (evita duplicar la misma acción). */}
      <a
        href={`https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent(WHATSAPP_DEMO_TEXT)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackContact}
        aria-label="Escríbenos por WhatsApp"
        className="hidden md:flex fixed bottom-6 right-6 z-40 h-14 w-14 items-center justify-center rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg transition-colors"
      >
        <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      </a>

      {/* Barra fija inferior — solo en móvil (<768px) */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white border-t border-gray-200 px-4 py-2.5 flex items-center gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
        <Link
          to="/register?plan=trial"
          onClick={trackLead}
          className="flex-1 text-center bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg py-2.5 text-sm"
        >
          Pruébalo gratis
        </Link>
        <a
          href={`https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent(WHATSAPP_DEMO_TEXT)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackContact}
          className="flex-1 text-center bg-green-50 hover:bg-green-100 text-green-700 font-bold rounded-lg py-2.5 text-sm border border-green-200"
        >
          WhatsApp
        </a>
      </div>
    </div>
  );
}

export default LandingPage;
